import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { RadioGroup, Text } from "@pentabd/ui";

import {
  InputDateControllerRow,
  InputTextRow,
  SelectControllerRow,
} from "@components";

import { FORM_FIELDS } from "@constants/form";
import { MASTER_DATA } from "@constants/master-data";

import { useMasterData } from "@hooks/useMasterData";
import { useLanguage } from "@hooks/useLanguage";

import { GeneralInfoFormDataType } from "@validations/HRM/employee/generalInfoValidation";
import { useEffect, useState } from "react";
import { getConvertedMasterData } from "@helpers/masterDataConvert";

const GENERAL_INFO = FORM_FIELDS.EMPLOYEE.GENERAL_INFO;

const { EMPLOYMENT_TYPE } = MASTER_DATA;

export const JoiningInfo = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const [expiryDateRequired, setExpiryDateRequired] = useState<boolean>(false);

  const { data: employmentType } = useMasterData(EMPLOYMENT_TYPE);

  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<GeneralInfoFormDataType>();

  const onProbation = watch(GENERAL_INFO.ON_PROBATION) as string;
  const seeRequired = watch(GENERAL_INFO.EMPLOYEE_TYPE) as number;

  useEffect(() => {
    if (seeRequired === 3) {
      setExpiryDateRequired(true);
    }
  }, [seeRequired]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (
        name === GENERAL_INFO.FREEDOM_FIGHTER ||
        name === GENERAL_INFO.DATE_OF_BIRTH
      ) {
        if (
          value[GENERAL_INFO.FREEDOM_FIGHTER] === true &&
          value[GENERAL_INFO.DATE_OF_BIRTH]
        ) {
          const PRLDate = dayjs(value[GENERAL_INFO.DATE_OF_BIRTH] as string)
            .add(60, "year")
            .format("YYYY-MM-DD")
            .toString();

          setValue(GENERAL_INFO.PRL_DATE, PRLDate);
        } else if (
          value[GENERAL_INFO.FREEDOM_FIGHTER] === false &&
          value[GENERAL_INFO.DATE_OF_BIRTH]
        ) {
          const PRLDate = dayjs(value[GENERAL_INFO.DATE_OF_BIRTH] as string)
            .add(59, "year")
            .format("YYYY-MM-DD")
            .toString();

          setValue(GENERAL_INFO.PRL_DATE, PRLDate);
        }
      }
      if (name === GENERAL_INFO.EMPLOYEE_TYPE) {
        if (value[GENERAL_INFO.EMPLOYEE_TYPE] === 3) {
          setExpiryDateRequired(true);
        } else {
          setExpiryDateRequired(false);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <div className="d-flex flex-column gap-8">
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.EMPLOYMENT_TYPE"
        control={control}
        name={GENERAL_INFO.EMPLOYEE_TYPE}
        register={{ ...register(GENERAL_INFO.EMPLOYEE_TYPE) }}
        options={getConvertedMasterData(employmentType, language)}
        setValue={setValue}
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.JOINING_DATE"
        control={control}
        register={{ ...register(GENERAL_INFO.JOINING_DATE) }}
        errors={errors}
        name={GENERAL_INFO.JOINING_DATE}
        required
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.CONTRACT_EXPIRY_DATE"
        control={control}
        register={{ ...register(GENERAL_INFO.CONTRACT_EXPIRY_DATE) }}
        errors={errors}
        name={GENERAL_INFO.CONTRACT_EXPIRY_DATE}
        required={expiryDateRequired}
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.DATE_OF_RESPONSIBILITY"
        control={control}
        register={{ ...register(GENERAL_INFO.DATE_OF_RESPONSIBILITY_TAKEOVER) }}
        errors={errors}
        name={GENERAL_INFO.DATE_OF_RESPONSIBILITY_TAKEOVER}
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.PRL_DATE"
        control={control}
        register={{ ...register(GENERAL_INFO.PRL_DATE) }}
        errors={errors}
        name={GENERAL_INFO.PRL_DATE}
        disabled
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.CONFIRMATION_GO_DATE"
        control={control}
        register={{ ...register(GENERAL_INFO.CONFIRMATION_GO_DATE) }}
        errors={errors}
        name={GENERAL_INFO.CONFIRMATION_GO_DATE}
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.GO_DATE"
        control={control}
        register={{ ...register(GENERAL_INFO.GO_DATE) }}
        errors={errors}
        name={GENERAL_INFO.GO_DATE}
      />

      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.GO_MEMO_NO"
        register={{ ...register(GENERAL_INFO.GO_MEMO_NO) }}
        error={errors}
        // restriction
      />

      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.LAST_PROMOTION_DATE"
        control={control}
        register={{ ...register(GENERAL_INFO.LAST_PROMOTION_DATE) }}
        errors={errors}
        name={GENERAL_INFO.LAST_PROMOTION_DATE}
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.DEPUTATION_DATE"
        control={control}
        register={{ ...register(GENERAL_INFO.DEPUTATION_DATE) }}
        errors={errors}
        name={GENERAL_INFO.DEPUTATION_DATE}
      />
      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.ORGANIZATION"
        register={{ ...register(GENERAL_INFO.ORGANIZATION) }}
        error={errors}
        // restriction
      />

      <div className="row">
        <div className="col-md-12 col-lg-3">
          <Text weight="semibold" size="sm" color="title">
            {t("EMPLOYEE.GENERAL_INFO.FORM.ON_PROBATION.LABEL")}
          </Text>
        </div>
        <div className="col-md-12 col-lg-7 d-flex flex-column gap-4">
          <Controller
            control={control}
            name={GENERAL_INFO.ON_PROBATION}
            render={({ field }) => {
              return (
                // <div className="d-flex align-items-center gap-9">
                //   <Switch
                //     {...register(GENERAL_INFO.ON_PROBATION)}
                //     id="on_probation"
                //     name={GENERAL_INFO.ON_PROBATION}
                //     size="sm"
                //     controlling
                //     onChange={(e) =>
                //       setValue(GENERAL_INFO.ON_PROBATION, e.target.checked)
                //     }
                //   />
                //   <Text color="subtitle1">
                //     {field.value === true
                //       ? t("EMPLOYEE.GENERAL_INFO.FORM.ON_PROBATION.YES")
                //       : t("EMPLOYEE.GENERAL_INFO.FORM.ON_PROBATION.NO")}
                //   </Text>
                // </div>
                <div className="d-flex gap-8 flex-wrap">
                  <RadioGroup
                    {...register(GENERAL_INFO.ON_PROBATION)}
                    name={GENERAL_INFO.ON_PROBATION}
                    id="on_probation"
                    options={[
                      {
                        id: "1",
                        label: `${t(
                          "EMPLOYEE.GENERAL_INFO.FORM.ON_PROBATION.YES",
                        )}`,
                        value: "yes",
                      },
                      {
                        id: "2",
                        label: `${t(
                          "EMPLOYEE.GENERAL_INFO.FORM.ON_PROBATION.NO",
                        )}`,
                        value: "no",
                      },
                    ]}
                    error={errors as any}
                  />
                </div>
              );
            }}
          />
        </div>
      </div>

      {onProbation && onProbation === "yes" && (
        <InputDateControllerRow
          label="EMPLOYEE.GENERAL_INFO.FORM.PROBATION_END_DATE"
          control={control}
          register={{ ...register(GENERAL_INFO.PROBATION_END_DATE) }}
          errors={errors as any}
          name={GENERAL_INFO.PROBATION_END_DATE}
        />
      )}
    </div>
  );
};
